import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Upload, FileText, Trash2, Scissors, Loader2, Clock, Crown, Sparkles, Library, Pencil, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import FloatingShapes from "@/components/FloatingShapes";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const GENRES = ["Biology", "Medicine", "Health", "Psychology", "Law", "Business", "Finance",
                "Economics", "History", "Politics", "Technology", "Engineering", "Science",
                "Math", "Literature", "Language", "Philosophy", "Religion", "Spirituality",
                "Yoga", "Art", "Music", "Self-Help", "Cooking", "Geography", "Other"];

const TIER_LABEL = { free: "Free", pro_monthly: "Pro", pro_annual: "Pro", lifetime: "Lifetime" };

export default function Dashboard() {
  const { user, setAuthUser } = useAuth();
  const isInnerCircle = user && (user.tier === "lifetime" || user.founding_member);
  const [docs, setDocs] = useState([]);
  const [shared, setShared] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stats, setStats] = useState(null);
  const fileRef = useRef();

  const load = async () => {
    try { const r = await api.get("/documents"); setDocs(r.data); }
    catch (e) {
      if (e?.response?.status === 401) {
        // Only bounce to /login when there's genuinely no token. A transient 401
        // (e.g. during the 5s poll) shouldn't log out a user who still has a token.
        if (!localStorage.getItem("loom_token")) {
          window.location.href = "/login";
        }
        return;
      }
      toast.error("Couldn't load your decks");
    }
  };

  const loadShared = async () => {
    try { const r = await api.get("/group/shared-decks"); setShared(r.data); }
    catch { /* shared decks are optional */ }
  };

  const loadStats = async () => {
    if (!user?.is_admin) return;
    try { const r = await api.get("/admin/paywall-stats"); setStats(r.data); }
    catch { /* admin only */ }
  };

  useEffect(() => {
    load(); loadShared(); loadStats();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [user?.is_admin]);

  const onPick = () => fileRef.current?.click();

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) { toast.error("PDFs only, friend."); return; }
    const fd = new FormData(); fd.append("file", f);
    setUploading(true); setProgress(0);
    try {
      const r = await api.post("/documents/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => setProgress(Math.round((ev.loaded * 100) / (ev.total || 1))),
      });
      toast.success(r.data.status === "needs_chunking"
        ? "Big book! Pick a chunk to start." : "Upload done. Brewing sections…");
      load();
    } catch (err) {
      const d = err?.response?.data?.detail || "";
      // The referral popup handles the free-limit case (402 w/ "bonus upload").
      if (!(err?.response?.status === 402 && /bonus upload/i.test(d))) {
        toast.error(d || "Upload failed");
      }
    } finally {
      setUploading(false); setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const doDelete = async (id) => {
    try { await api.delete(`/documents/${id}`); toast.success("Deck removed"); load(); }
    catch { toast.error("Couldn't delete"); }
  };
  const remove = (id, name) => {
    toast("Delete this deck?", {
      description: name,
      duration: 8000,
      action: { label: "Delete", onClick: () => doDelete(id) },
      cancel: { label: "Keep" },
    });
  };

  const setGenre = async (id, genre) => {
    setDocs((prev) => prev.map((d) => d.doc_id === id ? { ...d, genre } : d));
    try { await api.patch(`/documents/${id}/genre`, { genre }); }
    catch { toast.error("Couldn't move deck"); load(); }
  };

  // In-app prompt dialog (window.prompt is blocked inside the embedded preview)
  const [promptState, setPromptState] = useState(null);
  const askPrompt = (cfg) => setPromptState({ ...cfg, value: cfg.initial || "" });

  const renameShelf = (oldName) => askPrompt({
    title: `Rename "${oldName}"`, label: "Shelf name", initial: oldName,
    onSubmit: async (raw) => {
      const newName = (raw || "").trim();
      if (!newName || newName === oldName) return;
      setDocs((prev) => prev.map((d) => d.genre === oldName ? { ...d, genre: newName } : d));
      try { await api.post("/genres/rename", { old: oldName, new: newName }); toast.success(`Renamed to "${newName}"`); }
      catch { toast.error("Couldn't rename shelf"); load(); }
    },
  });

  const newGenreFor = (docId) => askPrompt({
    title: "New shelf", label: "Shelf name (e.g. Spirituality, Yoga)", initial: "",
    onSubmit: (raw) => { const name = (raw || "").trim(); if (name) setGenre(docId, name); },
  });

  const switchTier = async (tier) => {
    try {
      const r = await api.post("/admin/preview-tier", { tier });
      setAuthUser(r.data);
      toast.success(`Now previewing as ${TIER_LABEL[tier]}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't switch tier");
    }
  };

  const ready = docs.filter((d) => d.status === "ready");
  const pending = docs.filter((d) => d.status !== "ready");

  // group ready decks into genre shelves
  const shelves = {};
  ready.forEach((d) => {
    const g = (d.genre && d.genre.trim()) || "Unsorted";
    (shelves[g] = shelves[g] || []).push(d);
  });
  const shelfOrder = [...GENRES.filter((g) => shelves[g]),
                      ...Object.keys(shelves).filter((g) => !GENRES.includes(g))];
  // all genre names the user can pick from (defaults + their custom shelves)
  const allGenres = Array.from(new Set([...GENRES, ...Object.keys(shelves)]))
                         .filter((g) => g !== "Unsorted");

  return (
    <div className="relative min-h-screen grain" data-testid="dashboard-page">
      <FloatingShapes /><Nav />
      <main className="relative z-10 max-w-6xl mx-auto px-6 pt-32 pb-24">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <div>
            <span className="tag-pill">your atelier</span>
            <h1 className="font-display text-5xl sm:text-6xl mt-4">My shelves</h1>
            <p className="font-hand text-stone-500 text-2xl mt-2">decks, racked by what they're about</p>
            {isInnerCircle && (
              <Link to="/circle" className="inline-flex items-center gap-1.5 mt-4 text-xs uppercase tracking-wider px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition" data-testid="dashboard-inner-circle-badge">
                <Crown className="w-3.5 h-3.5"/> Inner Circle · manage seats
              </Link>
            )}
          </div>
          <div>
            <button onClick={onPick} disabled={uploading} className="btn-primary inline-flex items-center gap-2" data-testid="upload-pdf-button">
              {uploading ? <><Loader2 className="w-4 h-4 animate-spin"/> Uploading {progress}%</> : <><Upload className="w-4 h-4"/> Upload PDF</>}
            </button>
            <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={onUpload} data-testid="upload-pdf-input"/>
          </div>
        </div>

        {user?.is_admin && (
          <div className="mt-7 inline-flex items-center gap-2 bg-white/70 border border-stone-200 rounded-full px-3 py-2" data-testid="admin-tier-switcher">
            <span className="text-xs uppercase tracking-wider text-stone-500 px-1">Preview as</span>
            {[["free","Free"],["pro_monthly","Pro"],["lifetime","Lifetime"]].map(([val,label]) => {
              const activeTier = user.tier === "pro_annual" ? "pro_monthly" : user.tier;
              const isActive = activeTier === val;
              return (
                <button key={val} onClick={()=>switchTier(val)}
                        className={`text-sm px-3 py-1 rounded-full transition ${isActive ? "bg-indigo-500 text-white" : "text-stone-600 hover:bg-stone-100"}`}
                        data-testid={`tier-switch-${val}`}>
                  {label}
                </button>
              );
            })}
          </div>
        )}

        {user?.is_admin && stats && (
          <div className="mt-5 sticky-card p-6" data-testid="admin-paywall-stats">
            <div className="flex items-center gap-2 text-stone-700">
              <TrendingUp className="w-5 h-5 text-indigo-500"/>
              <span className="font-display text-2xl">Upgrade signals</span>
              <span className="font-hand text-stone-400 text-xl ml-1">where Free users hit the wall</span>
            </div>
            <div className="mt-4 flex flex-wrap gap-6">
              <div data-testid="stat-total-hits">
                <div className="font-display text-3xl">{stats.total_paywall_hits}</div>
                <div className="text-xs uppercase tracking-wider text-stone-500">paywall hits</div>
              </div>
              <div data-testid="stat-upgrade-clicks">
                <div className="font-display text-3xl text-indigo-600">{stats.upgrade_clicks}</div>
                <div className="text-xs uppercase tracking-wider text-stone-500">upgrade clicks</div>
              </div>
              {stats.by_feature?.length > 0 && (
                <div className="flex-1 min-w-[200px]">
                  <div className="text-xs uppercase tracking-wider text-stone-500 mb-1">by feature</div>
                  <div className="flex flex-wrap gap-2">
                    {stats.by_feature.map((f) => (
                      <span key={f.feature} className="tag-pill">{f.feature} · {f.count}</span>
                    ))}
                  </div>
                </div>
              )}
              {stats.top_decks?.length > 0 && (
                <div className="flex-1 min-w-[200px]">
                  <div className="text-xs uppercase tracking-wider text-stone-500 mb-1">top decks driving upgrades</div>
                  <div className="flex flex-wrap gap-2">
                    {stats.top_decks.slice(0,5).map((t) => (
                      <span key={t.deck} className="tag-pill">{t.deck} · {t.count}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {docs.length === 0 ? (
          <motion.div initial={{opacity:0, y:24}} animate={{opacity:1, y:0}}
                      className="sticky-card mt-12 p-16 text-center" data-testid="empty-state">
            <Library className="w-16 h-16 text-stone-300 mx-auto"/>
            <div className="font-display text-3xl mt-6">No paper yet</div>
            <p className="font-hand text-stone-500 text-xl mt-2">your first deck is one drag away.</p>
            <button onClick={onPick} className="btn-primary mt-6" data-testid="empty-upload-button">Drop your first PDF</button>
          </motion.div>
        ) : (
          <div className="mt-12 space-y-12">
            {pending.length > 0 && (
              <Shelf title="In the kiln" hint="being sketched into sections" testid="shelf-pending">
                {pending.map((d) => (
                  <DeckCard key={d.doc_id} d={d} onRemove={remove} />
                ))}
              </Shelf>
            )}
            {shelfOrder.map((g) => (
              <Shelf key={g} title={g} hint={`${shelves[g].length} deck${shelves[g].length>1?"s":""}`}
                     testid={`shelf-${g.toLowerCase()}`} onRename={()=>renameShelf(g)}>
                {shelves[g].map((d) => (
                  <DeckCard key={d.doc_id} d={d} onRemove={remove} onGenre={setGenre} onNewGenre={newGenreFor} genres={allGenres} />
                ))}
              </Shelf>
            ))}
          </div>
        )}

        {shared.length > 0 && (
          <section className="mt-20" data-testid="shared-with-you">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-indigo-500"/>
              <h2 className="font-display text-3xl">Shared with you</h2>
            </div>
            <p className="font-hand text-stone-500 text-xl mt-1">decks lent from study circles you belong to</p>
            <div className="flex gap-6 overflow-x-auto pb-4 mt-6 rack" data-testid="shared-deck-grid">
              {shared.map((d) => (
                <div key={d.doc_id} className="sticky-card p-7 relative shrink-0 w-[300px]" data-testid={`shared-deck-card-${d.doc_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2 text-stone-500"><FileText className="w-4 h-4"/><span className="text-xs">{d.page_count} pages</span></div>
                    <span className="text-xs uppercase tracking-wider px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700 inline-flex items-center gap-1"><Sparkles className="w-3 h-3"/> {d.shared_from}</span>
                  </div>
                  <div className="font-display text-2xl mt-3 line-clamp-2 leading-snug">{d.name}</div>
                  {d.chunk_label && <div className="font-hand text-stone-500 text-lg mt-1">{d.chunk_label}</div>}
                  <div className="text-stone-600 text-sm mt-3">{d.sections?.length || 0} sections · shared library</div>
                  <div className="mt-6">
                    <Link to={`/deck/${d.doc_id}`} className="btn-primary text-sm" data-testid={`open-shared-deck-${d.doc_id}`}>Open deck →</Link>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      <Dialog open={!!promptState} onOpenChange={(o)=>{ if(!o) setPromptState(null); }}>
        <DialogContent data-testid="prompt-dialog">
          <DialogHeader>
            <DialogTitle>{promptState?.title}</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <label className="text-sm text-stone-500">{promptState?.label}</label>
            <Input
              autoFocus
              value={promptState?.value || ""}
              onChange={(e)=>setPromptState((p)=>({ ...p, value: e.target.value }))}
              onKeyDown={(e)=>{ if(e.key==="Enter"){ const cb=promptState?.onSubmit; const v=promptState?.value; setPromptState(null); cb && cb(v); } }}
              className="mt-2"
              data-testid="prompt-dialog-input"
            />
          </div>
          <DialogFooter>
            <button onClick={()=>setPromptState(null)} className="btn-ghost text-sm" data-testid="prompt-dialog-cancel">Cancel</button>
            <button onClick={()=>{ const cb=promptState?.onSubmit; const v=promptState?.value; setPromptState(null); cb && cb(v); }} className="btn-primary text-sm" data-testid="prompt-dialog-save">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Shelf({ title, hint, testid, onRename, children }) {
  return (
    <section data-testid={testid}>
      <div className="flex items-baseline gap-3 border-b-2 border-stone-200/70 pb-2 group">
        <h2 className="font-display text-3xl">{title}</h2>
        <span className="font-hand text-stone-400 text-xl">{hint}</span>
        {onRename && (
          <button onClick={onRename}
                  className="text-stone-300 hover:text-indigo-500 transition opacity-0 group-hover:opacity-100"
                  title="Rename shelf" data-testid={`rename-shelf-${title.toLowerCase()}`}>
            <Pencil className="w-4 h-4"/>
          </button>
        )}
      </div>
      <div className="flex gap-6 overflow-x-auto pt-6 pb-4 rack">
        {children}
      </div>
    </section>
  );
}

function statusPill(s) {
  const map = {
    ready:           { t: "ready",          c: "bg-emerald-100 text-emerald-700" },
    processing:      { t: "drawing…",        c: "bg-indigo-100 text-indigo-700" },
    uploaded:        { t: "queued",          c: "bg-amber-100 text-amber-700" },
    needs_chunking:  { t: "needs chunking",  c: "bg-rose-100 text-rose-700" },
    failed:          { t: "failed",          c: "bg-red-100 text-red-700" },
  };
  const v = map[s] || { t: s, c: "bg-stone-100 text-stone-700" };
  return <span className={`text-xs uppercase tracking-wider px-2.5 py-1 rounded-full ${v.c}`}>{v.t}</span>;
}

function DeckCard({ d, onRemove, onGenre, onNewGenre, genres }) {
  const opts = genres || GENRES;
  const current = d.genre && d.genre.trim() ? d.genre : "Other";
  const list = opts.includes(current) ? opts : [current, ...opts];
  const onSelect = (e) => {
    const v = e.target.value;
    if (v === "__new__") { onNewGenre && onNewGenre(d.doc_id); return; }
    onGenre(d.doc_id, v);
  };
  return (
    <motion.div
      initial={{opacity:0, y:18}} animate={{opacity:1, y:0}}
      className="sticky-card p-7 relative shrink-0 w-[300px]"
      data-testid={`deck-card-${d.doc_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 text-stone-500"><FileText className="w-4 h-4"/><span className="text-xs">{d.page_count} pages</span></div>
        {statusPill(d.status)}
      </div>
      <div className="font-display text-2xl mt-3 line-clamp-2 leading-snug" data-testid="deck-title">{d.name}</div>
      {d.chunk_label && <div className="font-hand text-stone-500 text-lg mt-1">{d.chunk_label}</div>}

      {d.status === "ready" && (
        <div className="text-stone-600 text-sm mt-3">{d.sections?.length || 0} sections · ready to wander</div>
      )}
      {d.status === "processing" && (
        <div className="text-indigo-600 text-sm mt-3 inline-flex items-center gap-2"><Clock className="w-4 h-4 animate-pulse"/> sketching sections…</div>
      )}
      {d.status === "uploaded" && (<div className="text-amber-600 text-sm mt-3">queued</div>)}
      {d.status === "needs_chunking" && (
        <div className="mt-3">
          <div className="text-rose-600 text-sm flex items-center gap-2"><Scissors className="w-4 h-4"/> Too long — pick a chunk:</div>
          <div className="mt-3 flex flex-wrap gap-2 max-h-32 overflow-auto">
            {(d.recommended_chunks||[]).map((c) => (
              <ChunkButton key={c.label} doc={d} chunk={c} />
            ))}
          </div>
        </div>
      )}
      {d.status === "failed" && (<div className="text-red-600 text-sm mt-3">⚠ {d.error || "Something went sideways."}</div>)}

      {d.status === "ready" && onGenre && (
        <select
          value={current}
          onChange={onSelect}
          className="mt-4 w-full text-sm rounded-xl border border-stone-200 bg-white/80 px-3 py-2 outline-none focus:border-indigo-300"
          data-testid={`genre-select-${d.doc_id}`}
          onClick={(e)=>e.stopPropagation()}
        >
          {list.map((g) => <option key={g} value={g}>{g}</option>)}
          <option value="__new__">+ New shelf…</option>
        </select>
      )}

      <div className="flex items-center justify-between mt-6">
        {d.status === "ready" ? (
          <Link to={`/deck/${d.doc_id}`} className="btn-primary text-sm" data-testid={`open-deck-${d.doc_id}`}>Open deck →</Link>
        ) : <span/>}
        <button onClick={()=>onRemove(d.doc_id, d.name)} className="text-stone-400 hover:text-rose-500" data-testid={`delete-deck-${d.doc_id}`}>
          <Trash2 className="w-4 h-4"/>
        </button>
      </div>
    </motion.div>
  );
}

function ChunkButton({ doc, chunk }) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          const fd = new FormData();
          fd.append("start", chunk.start); fd.append("end", chunk.end);
          await api.post(`/documents/${doc.doc_id}/process_chunk`, fd);
          toast.success(`Brewing ${chunk.label}…`);
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
      }}
      className="text-xs px-3 py-1.5 rounded-full bg-white border border-stone-200 hover:bg-rose-50 hover:border-rose-200 transition"
      data-testid={`chunk-${doc.doc_id}-${chunk.start}-${chunk.end}`}
    >
      {busy ? "…" : chunk.label}
    </button>
  );
}
